/**
 * Vertiv React Native App
 * Cross-platform application for managing requests
 *
 * @format
 */

import * as Sentry from '@sentry/react-native';
import { QueryClientProvider } from '@tanstack/react-query';
import React, {
  useEffect,
  useState,
  Suspense,
  useRef,
  useCallback,
  useSyncExternalStore,
} from 'react';
import {
  StatusBar,
  useColorScheme,
  Platform,
  View,
  ActivityIndicator,
  Dimensions,
  Text,
  AppState,
  AppStateStatus,
  BackHandler,
} from 'react-native';
import { SafeAreaProvider } from 'react-native-safe-area-context';

import { RequestLayoutShell } from './src/components';
import { AppUpdateScreen } from './src/components/AppUpdateScreen';
import { FlavorBanner } from './src/components/FlavorBanner';
import { TermsConditionsModal } from './src/components/TermsConditionsModal';
import {
  bindFeedbackCaptureRelease,
  feedbackPageRef,
  holdFeedbackNavigation,
  isFeedbackCaptureOpen,
} from './src/components/userFeedback/feedbackCapture';
import { FeedbackCaptureHost } from './src/components/userFeedback/ScreenCapture';
import {
  getSentryClientOptions,
  getSentryEnvironment,
  isSentryEnabled,
} from './src/config/env';
import { validateEnvironment } from './src/config/env';
import { isAppUpdateRoute } from './src/constants/appUpdate';
import { REQUEST_FORM_MOBILE_LAYOUT_MAX_WIDTH } from './src/constants/requestFormLayout';
import { AuthProvider, useAuth } from './src/contexts/AuthContext';
import { LocationTagsDraftProvider } from './src/contexts/LocationTagsDraftContext';
import {
  PreferencesApprovalsLeaveGuardProvider,
  usePreferencesApprovalsLeaveGuard,
} from './src/contexts/PreferencesApprovalsLeaveGuardContext';
import {
  RequestLeaveGuardProvider,
  useRequestLeaveGuard,
} from './src/contexts/RequestLeaveGuardContext';
import {
  createNewRequestScopeRegistration,
  useRegisterRequestScope,
  useRequestScope,
  type RequestScopeRegistration,
  type RequestScopeSnapshot,
} from './src/contexts/RequestScopeContext';
import { SidebarStepMetricsProvider } from './src/contexts/SidebarStepMetricsContext';
import {
  registrationFingerprint,
  scopeLoopLog,
  summarizeStepIds,
  useScopeLoopRenderCounter,
} from './src/dev/scopeLoopDebug';
import { queryClient } from './src/lib/queryClient';
import {
  clearCreateRequestDraftSnapshot,
  isFreshCreateRequestQuery,
  stripFreshCreateRequestParam,
} from './src/services/requestDraftSnapshot';
import { normalizeRequestFormStepId } from './src/utils/formStepIds';

import type { RequestFormStepId } from './src/config/requestFormStepRegistry';

const SENTRY_DSN =
  'https://56557d89232d5bc82402077c0f59e113@o4511185068490752.ingest.us.sentry.io/4511185076551680';

const sentryEnvironment = getSentryEnvironment();
const sentryClientOptions = getSentryClientOptions(sentryEnvironment);

Sentry.init({
  dsn: isSentryEnabled() ? SENTRY_DSN : '',
  environment: sentryEnvironment,
  enableNativeCrashHandling: true,
  enableWatchdogTerminationTracking: true,
  sendDefaultPii: sentryClientOptions.sendDefaultPii,
  enableLogs: sentryClientOptions.enableLogs,
  replaysSessionSampleRate: sentryClientOptions.replaysSessionSampleRate,
  replaysOnErrorSampleRate: sentryClientOptions.replaysOnErrorSampleRate,
  integrations: [Sentry.mobileReplayIntegration()],
  beforeSend(event) {
    const message = event.message || event.exception?.values?.[0]?.value || '';

    // Filter noisy errors
    const noisePatterns = [
      'ServiceWorker',
      'rootTag',
      'Failed to fetch',
      'Network request failed',
      'chrome-extension://',
    ];

    if (noisePatterns.some(pattern => message.includes(pattern))) {
      return null;
    }

    return event;
  },
});

if (isSentryEnabled()) {
  void Sentry.crashedLastRun().then(crashed => {
    if (crashed) {
      Sentry.captureMessage(
        'Native crash from previous session uploaded on relaunch',
      );
    }
  });
}

// Platform-specific imports: Use React.lazy for web, regular imports for native
// This enables code splitting on web while maintaining compatibility with native platforms
let Login: React.ComponentType<any>;
let AppUpdate: React.ComponentType<any>;
let AccessRequestSuccess: React.ComponentType<any>;
let InternalUserAccess: React.ComponentType<any>;
let ViewRequests: React.ComponentType<any>;
let RoleManagement: React.ComponentType<any>;
let RequestDetail: React.ComponentType<any>;
let CreateRequest: React.ComponentType<any>;
let UnauthorizedAccess: React.ComponentType<any>;
let Preferences: React.ComponentType<any>;
let PromptManagement: React.ComponentType<any>;
let FeedbackView: React.ComponentType<any>;
let TermsConditionsView: React.ComponentType<any>;
let UserProfile: React.ComponentType<any>;
let VersionHistory: React.ComponentType<any>;

if (Platform.OS === 'web') {
  // Web: Use React.lazy for code splitting
  Login = React.lazy(() =>
    import('./src/screens/Login').then(m => ({ default: m.Login })),
  );
  AppUpdate = React.lazy(() =>
    import('./src/screens/AppUpdate').then(m => ({ default: m.AppUpdate })),
  );
  AccessRequestSuccess = React.lazy(() =>
    import('./src/screens/AccessRequestSuccess').then(m => ({
      default: m.AccessRequestSuccess,
    })),
  );
  InternalUserAccess = React.lazy(() =>
    import('./src/screens/InternalUserAccess').then(m => ({
      default: m.InternalUserAccess,
    })),
  );
  ViewRequests = React.lazy(() =>
    import('./src/screens/ViewRequests').then(m => ({
      default: m.ViewRequests,
    })),
  );
  RoleManagement = React.lazy(() =>
    import('./src/screens/RoleManagement').then(m => ({
      default: m.RoleManagement,
    })),
  );
  RequestDetail = React.lazy(() =>
    import('./src/screens/RequestDetail').then(m => ({
      default: m.RequestDetail,
    })),
  );
  CreateRequest = React.lazy(() =>
    import('./src/screens/CreateRequest').then(m => ({
      default: m.CreateRequest,
    })),
  );
  UnauthorizedAccess = React.lazy(() =>
    import('./src/screens/UnauthorizedAccess').then(m => ({
      default: m.UnauthorizedAccess,
    })),
  );
  Preferences = React.lazy(() =>
    import('./src/screens/Preferences').then(m => ({ default: m.Preferences })),
  );
  PromptManagement = React.lazy(() =>
    import('./src/screens/PromptManagement').then(m => ({
      default: m.PromptManagement,
    })),
  );
  FeedbackView = React.lazy(() =>
    import('./src/screens/FeedbackView').then(m => ({
      default: m.FeedbackView,
    })),
  );
  TermsConditionsView = React.lazy(() =>
    import('./src/screens/TermsConditionsView').then(m => ({
      default: m.TermsConditionsView,
    })),
  );
  UserProfile = React.lazy(() =>
    import('./src/screens/UserProfile').then(m => ({ default: m.UserProfile })),
  );
  VersionHistory = React.lazy(() =>
    import('./src/screens/VersionHistory').then(m => ({
      default: m.VersionHistory,
    })),
  );
} else {
  // Native: Use regular imports (React.lazy not supported)
  const LoginModule = require('./src/screens/Login');
  const AppUpdateModule = require('./src/screens/AppUpdate');
  const AccessRequestSuccessModule = require('./src/screens/AccessRequestSuccess');
  const InternalUserAccessModule = require('./src/screens/InternalUserAccess');
  const ViewRequestsModule = require('./src/screens/ViewRequests');
  const RoleManagementModule = require('./src/screens/RoleManagement');
  const RequestDetailModule = require('./src/screens/RequestDetail');
  const CreateRequestModule = require('./src/screens/CreateRequest');
  const UnauthorizedAccessModule = require('./src/screens/UnauthorizedAccess');
  const PreferencesModule = require('./src/screens/Preferences');
  const PromptManagementModule = require('./src/screens/PromptManagement');
  const FeedbackViewModule = require('./src/screens/FeedbackView');
  const TermsConditionsViewModule = require('./src/screens/TermsConditionsView');
  const UserProfileModule = require('./src/screens/UserProfile');
  const VersionHistoryModule = require('./src/screens/VersionHistory');

  Login = LoginModule.Login;
  AppUpdate = AppUpdateModule.AppUpdate;
  AccessRequestSuccess = AccessRequestSuccessModule.AccessRequestSuccess;
  InternalUserAccess = InternalUserAccessModule.InternalUserAccess;
  ViewRequests = ViewRequestsModule.ViewRequests;
  RoleManagement = RoleManagementModule.RoleManagement;
  RequestDetail = RequestDetailModule.RequestDetail;
  CreateRequest = CreateRequestModule.CreateRequest;
  UnauthorizedAccess = UnauthorizedAccessModule.UnauthorizedAccess;
  Preferences = PreferencesModule.Preferences;
  PromptManagement = PromptManagementModule.PromptManagement;
  FeedbackView = FeedbackViewModule.FeedbackView;
  TermsConditionsView = TermsConditionsViewModule.TermsConditionsView;
  UserProfile = UserProfileModule.UserProfile;
  VersionHistory = VersionHistoryModule.VersionHistory;
}
import {
  initializeOfflineServices,
  cleanupOfflineServices,
} from './src/services/offlineInit';
import { UOMProvider } from './src/contexts/UOMContext';
import { FormEditingProvider } from './src/contexts/FormEditingContext';
import {
  TermsConditionsProvider,
  useTermsConditions,
} from './src/contexts/TermsConditionsContext';
import {
  ImpersonationProvider,
  useImpersonation,
} from './src/contexts/ImpersonationContext';
import { roleService } from './src/services/auth/roleService';
import { useRequestStage } from './src/hooks/useRequestStage';
import { returnUrlService } from './src/services/returnUrlService';
import { deepLinkService } from './src/services/deepLinkService';
import { deepLinkAction } from './src/services/deepLinkRouting';
import { logEnvironmentInfo } from './src/utils/debugEnv';
import { audioRecordingService } from './src/services/audio';
import {
  getDisplayTimeZone,
  subscribeDisplayTimeZone,
} from './src/services/displayTimeZone';
import { showToast } from './src/utils/toast';
import { useAudioRecording } from './src/hooks/useAudioRecording';
import { usePendingToast } from './src/hooks/usePendingToast';
import { featureFlags } from './src/config/featureFlags';

// Keyboard provider for native platforms (no-op on web)
let KeyboardProvider: React.ComponentType<{
  children: React.ReactNode;
}> | null = null;
if (Platform.OS !== 'web') {
  try {
    KeyboardProvider =
      require('react-native-keyboard-controller').KeyboardProvider;
  } catch (error) {
    console.warn('react-native-keyboard-controller not available:', error);
  }
}

// Loading fallback component for Suspense
const LoadingFallback = () => (
  <View
    style={{
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      backgroundColor: '#f5f5f5',
    }}
  >
    <ActivityIndicator size="large" color="#FF6B35" />
    <Text style={{ marginTop: 16, fontSize: 14, color: '#666' }}>
      Loading...
    </Text>
  </View>
);

// Conditional import for web-only toast component
let Toaster: any = null;
if (Platform.OS === 'web') {
  try {
    const reactHotToast = require('react-hot-toast');
    Toaster = reactHotToast.Toaster;
  } catch (error) {
    console.warn('react-hot-toast not available:', error);
  }
}
// Conditionally import react-native-toast-message for native platforms
let Toast: any = null;
if (Platform.OS !== 'web') {
  try {
    Toast = require('react-native-toast-message').default;
  } catch (error) {
    console.warn('react-native-toast-message not available:', error);
  }
}

/** True when navigating off Create Request / Request Detail (or to a different request). */
function isLeavingRequestFlow(
  currentPath: string,
  nextPath: string,
  currentRequestId: string | null,
  nextRequestId: string | null = null,
): boolean {
  const isOnRequestFlow =
    currentPath === '/create-request' || currentPath === '/request-detail';
  const isNextRequestFlow =
    nextPath === '/create-request' || nextPath === '/request-detail';

  return (
    isOnRequestFlow &&
    (!isNextRequestFlow ||
      (nextPath === '/request-detail' &&
        nextRequestId != null &&
        nextRequestId !== currentRequestId))
  );
}

/** Main-bundle bridge: lazy Create Request chunks may not share RequestScopeContext (#1330). */
function CreateRequestWithNavScope(
  props: Omit<
    import('./src/screens/CreateRequest').CreateRequestProps,
    'navScopeSnapshot' | 'onScopeRegistrationChange'
  >,
) {
  useScopeLoopRenderCounter('CreateRequestWithNavScope');
  const navScope = useRequestScope();
  const navScopeSnapshot = React.useMemo<RequestScopeSnapshot | null>(() => {
    if (!navScope) {
      return null;
    }
    return {
      storedStepIds: navScope.storedStepIds,
      enabledStepIds: navScope.enabledStepIds,
      removableStepIds: navScope.removableStepIds,
    };
  }, [
    navScope?.storedStepIds,
    navScope?.enabledStepIds,
    navScope?.removableStepIds,
  ]);

  const scopeChangeBridgeRef = React.useRef<(ids: RequestFormStepId[]) => void>(
    () => {},
  );
  const [scopeRegistration, setScopeRegistration] =
    React.useState<RequestScopeRegistration>(() =>
      createNewRequestScopeRegistration(ids =>
        scopeChangeBridgeRef.current(ids),
      ),
    );
  useRegisterRequestScope(scopeRegistration);

  const handleScopeRegistrationChange = React.useCallback(
    (registration: RequestScopeRegistration | null) => {
      setScopeRegistration(prev => {
        if (registration?.onScopeChange) {
          scopeChangeBridgeRef.current = registration.onScopeChange;
        }
        if (prev === registration) {
          scopeLoopLog('App handleScopeRegistrationChange SKIP (same ref)', {
            fingerprint: registrationFingerprint(registration),
          });
          return prev;
        }
        if (prev == null || registration == null) {
          scopeLoopLog('App handleScopeRegistrationChange UPDATE (null edge)', {
            prev: prev == null ? 'null' : 'object',
            next: registration == null ? 'null' : 'object',
            fingerprint: registrationFingerprint(registration),
          });
          return registration;
        }
        if (
          prev.storedStepIds === registration.storedStepIds &&
          prev.showScopeControls === registration.showScopeControls &&
          prev.canManageScope === registration.canManageScope
        ) {
          scopeLoopLog(
            'App handleScopeRegistrationChange SKIP (equivalent primitives; stable handler via ref)',
            {
              storedStepIds: summarizeStepIds(registration.storedStepIds),
              showScopeControls: registration.showScopeControls,
              canManageScope: registration.canManageScope,
              onScopeChangeIdentityChanged:
                prev.onScopeChange !== registration.onScopeChange,
            },
          );
          return prev;
        }
        scopeLoopLog('App handleScopeRegistrationChange UPDATE', {
          prev: {
            storedStepIds: summarizeStepIds(prev.storedStepIds),
            showScopeControls: prev.showScopeControls,
            canManageScope: prev.canManageScope,
            onScopeChangeChanged:
              prev.onScopeChange !== registration.onScopeChange,
          },
          next: {
            storedStepIds: summarizeStepIds(registration.storedStepIds),
            showScopeControls: registration.showScopeControls,
            canManageScope: registration.canManageScope,
          },
        });
        return registration;
      });
    },
    [],
  );

  return (
    <CreateRequest
      {...props}
      navScopeSnapshot={navScopeSnapshot}
      onScopeRegistrationChange={handleScopeRegistrationChange}
    />
  );
}

function AppContent() {
  const isDarkMode = useColorScheme() === 'dark';
  const [currentRoute, setCurrentRoute] = useState('/request');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [leftSidebarVisible, setLeftSidebarVisible] = useState(false);
  const [screenWidth, setScreenWidth] = useState(
    Dimensions.get('window').width,
  );
  const { isAuthenticated, user, isLoading: authLoading } = useAuth();
  useSyncExternalStore(
    subscribeDisplayTimeZone,
    getDisplayTimeZone,
    getDisplayTimeZone,
  );
  const { requiresAcceptance: tcRequiresAcceptance, isLoading: tcLoading } =
    useTermsConditions();
  const { isImpersonating } = useImpersonation();
  const [currentRequestId, setCurrentRequestId] = useState<string | null>(null);
  const [currentFormStep, setCurrentFormStep] = useState<string>('opportunity');
  const requestLeaveGuard = useRequestLeaveGuard();
  const preferencesApprovalsLeaveGuard = usePreferencesApprovalsLeaveGuard();
  const currentRouteRef = useRef(currentRoute);
  currentRouteRef.current = currentRoute;
  const currentRequestIdRef = useRef(currentRequestId);
  currentRequestIdRef.current = currentRequestId;
  const requestLeaveGuardRef = useRef(requestLeaveGuard);
  requestLeaveGuardRef.current = requestLeaveGuard;
  const preferencesApprovalsLeaveGuardRef = useRef(
    preferencesApprovalsLeaveGuard,
  );
  preferencesApprovalsLeaveGuardRef.current = preferencesApprovalsLeaveGuard;

  const runPreferencesApprovalsLeaveGuard =
    React.useCallback(async (): Promise<boolean> => {
      if (!preferencesApprovalsLeaveGuardRef.current) {
        return true;
      }
      return preferencesApprovalsLeaveGuardRef.current.tryLeave();
    }, []);
  const setCurrentFormStepWithPersistence = React.useCallback(
    (stepId: string) => {
      void (async () => {
        if (stepId === currentFormStep) {
          return;
        }
        const guard = requestLeaveGuardRef.current;
        if (guard) {
          const mayChange = await guard.tryChangeRequestStep(stepId);
          if (!mayChange) {
            return;
          }
        }
        setCurrentFormStep(stepId);
        if (Platform.OS === 'web' && typeof window !== 'undefined') {
          const path = window.location.pathname;
          if (path === '/request-detail' || path === '/create-request') {
            const params = new URLSearchParams(window.location.search);
            params.set('step', stepId);
            window.history.replaceState({}, '', `${path}?${params.toString()}`);
          }
        }
      })();
    },
    [currentFormStep],
  );
  const [currentRequestStage, setCurrentRequestStage] =
    useState<string>('DRAFT');
  const [effectiveStageForSidebar, setEffectiveStageForSidebar] = useState<
    string | undefined
  >(undefined);
  const [hasInvalidatedOnAuth, setHasInvalidatedOnAuth] = useState(false);
  const [isInitialPageLoad, setIsInitialPageLoad] = useState(true);
  const prevUserIdRef = React.useRef<string | null>(null);

  // Audio recording hook for CreateRequest audio recording feature
  const {
    recordingState,
    elapsedTime,
    recordedAudioFile,
    handleStartRecording,
    handlePauseRecording,
    handleResumeRecording,
    handleAcceptRecording,
    handleDiscardRecording,
    forceCancelRecording,
  } = useAudioRecording();

  // Ref to track recording state for use in event handlers
  // Update synchronously to avoid race conditions where navigate is called before useEffect runs
  const recordingStateRef = useRef(recordingState);
  recordingStateRef.current = recordingState;

  // Handle visibility change (tab switch, app backgrounding) - cancel recording
  // Web: uses document.visibilitychange
  // Native: uses AppState change
  const appStateRef = useRef<AppStateStatus>(AppState.currentState);

  useEffect(() => {
    if (Platform.OS === 'web') {
      // Web: Handle tab visibility changes
      const handleVisibilityChange = async () => {
        // Check SERVICE state directly (not hook state) because RequestDetail has its own hook
        const serviceRecordingState = audioRecordingService.getRecordingState();
        if (
          document.visibilityState === 'hidden' &&
          serviceRecordingState !== 'idle'
        ) {
          await audioRecordingService.forceCancel();
          // Use infoPersistent so the toast displays when user returns to the app
          showToast.infoPersistent('Recording canceled');
        }
      };

      document.addEventListener('visibilitychange', handleVisibilityChange);
      return () => {
        document.removeEventListener(
          'visibilitychange',
          handleVisibilityChange,
        );
      };
    } else {
      // Native: Handle app state changes (background/inactive)
      const handleAppStateChange = async (nextAppState: AppStateStatus) => {
        // Check if app is going to background or becoming inactive
        if (
          appStateRef.current === 'active' &&
          (nextAppState === 'background' || nextAppState === 'inactive')
        ) {
          // Check SERVICE state directly (not hook state) because RequestDetail has its own hook
          const serviceRecordingState =
            audioRecordingService.getRecordingState();
          if (serviceRecordingState !== 'idle') {
            await audioRecordingService.forceCancel();
            // Show toast - on native, regular toast works fine
            showToast.info('Recording canceled');
          }
        }
        appStateRef.current = nextAppState;
      };

      const subscription = AppState.addEventListener(
        'change',
        handleAppStateChange,
      );
      return () => {
        subscription.remove();
      };
    }
  }, []); // No dependencies needed - we use the service singleton directly

  // Check for pending toasts after navigation (e.g., recording cancelled message)
  usePendingToast(currentRoute);

  // Validate and log environment info on mount
  useEffect(() => {
    // Validate environment variables
    validateEnvironment();
    // Log environment info (development only)
    logEnvironmentInfo();
  }, []);

  const handlePlayRecording = useCallback(async () => {
    if (!recordedAudioFile) {
      console.warn('No recorded audio file to play');
      return;
    }

    try {
      console.log('Playing recording:', recordedAudioFile.uri);
      // Use the audio recording service to play the file
      // Note: This is a quick test - in production you'd want a separate player service
      const result = await (audioRecordingService as any).recorder?.startPlayer(
        recordedAudioFile.uri,
      );
      console.log('Playback started:', result);
    } catch (error) {
      console.error('Playback error:', error);
    }
  }, [recordedAudioFile]);

  // Handle stage changes from child components
  const handleStageChange = (newStage: string) => {
    setCurrentRequestStage(newStage);
  };

  // Handle effective stage changes for sidebar filtering (basic/advanced view)
  const handleEffectiveStageChange = (effectiveStage: string | undefined) => {
    setEffectiveStageForSidebar(effectiveStage);
  };

  // For stage detection, we'll use a different approach since useRequestStage needs to be inside QueryClientProvider
  const isOnRequestDetail = currentRoute.split('?')[0] === '/request-detail';
  // We'll detect stage inside AppContentWithQuery component

  // Redirect legacy /role-management URL to /users (backwards compatibility)
  // Uses replaceState to avoid back-button infinite loop
  useEffect(() => {
    const pathname = currentRoute.split('?')[0];
    if (pathname === '/role-management') {
      if (Platform.OS === 'web') {
        window.history.replaceState({}, '', '/users');
      }
      setCurrentRoute('/users');
      return;
    }
  }, [currentRoute]);

  // Reset stage to DRAFT when on create-request or navigating away from request pages
  useEffect(() => {
    const pathname = currentRoute.split('?')[0];
    // Reset to DRAFT for create-request or when navigating away from request pages
    if (
      pathname === '/create-request' ||
      (pathname !== '/request-detail' && pathname !== '/create-request')
    ) {
      setCurrentRequestStage('DRAFT');
      setEffectiveStageForSidebar(undefined); // Also clear effective stage override
    }
  }, [currentRoute]);

  // Handle screen size changes
  useEffect(() => {
    const subscription = Dimensions.addEventListener('change', ({ window }) => {
      setScreenWidth(window.width);
    });

    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      const handleResize = () => {
        setScreenWidth(window.innerWidth);
      };

      window.addEventListener('resize', handleResize);

      return () => {
        subscription?.remove();
        window.removeEventListener('resize', handleResize);
      };
    }

    return () => subscription?.remove();
  }, []);

  // Determine if we should use mobile layout (overlay).
  // #1438: `spacing.formMaxWidth` is derived from this breakpoint, so it is a shared
  // constant rather than a literal — see `REQUEST_FORM_MOBILE_LAYOUT_MAX_WIDTH`.
  const isMobileLayout = screenWidth < REQUEST_FORM_MOBILE_LAYOUT_MAX_WIDTH;

  // Read by the mount-once deep-link effect below, which must see the latest
  // values without re-running (a re-run is what re-subscribed and re-delivered).
  const isAuthenticatedRef = useRef(isAuthenticated);
  isAuthenticatedRef.current = isAuthenticated;
  const navigateRef = useRef<(path: string) => Promise<void>>(async () => {});
  // Bumped only by a deep link to the request already on screen: it is
  // RequestDetail's `key`, so the screen re-mounts and reads the link's
  // `discussion`/`thread` as a fresh arrival (see `deepLinkRouting.ts`).
  const [requestDetailMountNonce, setRequestDetailMountNonce] = useState(0);
  // A link that arrived before sign-in, held in memory as well as saved as the
  // return URL: the post-auth redirect reads this first, so it cannot lose the
  // link to a session restore that finishes before the AsyncStorage write.
  const pendingDeepLinkRouteRef = useRef<string | null>(null);
  // One same-request re-open at a time (its leave prompt is awaited).
  const reopenInFlightRef = useRef(false);

  // Native deep links (#1599 Story 3.3). Mount-once on purpose: the service
  // delivers the cold-start URL once and holds one `Linking` subscription for
  // the process, and this listener reads auth, route and `navigate` through
  // refs. ⚠️ Do not add dependencies: a re-run used to re-deliver the
  // cold-start link, bouncing the user back to it after every request change.
  useEffect(() => {
    if (Platform.OS === 'web') {
      return;
    }

    const reopenRequestDetail = async (route: string) => {
      const guard = requestLeaveGuardRef.current;
      if (guard) {
        const mayLeave = await guard.tryLeaveRequest();
        if (!mayLeave) {
          return;
        }
      }
      // The prompt was awaited: the user may have signed out or moved to
      // another request meanwhile, so decide again on what is true now.
      const now = deepLinkAction({
        route,
        isAuthenticated: isAuthenticatedRef.current,
        currentRoute: currentRouteRef.current,
      });
      if (now === 'defer') {
        pendingDeepLinkRouteRef.current = route;
        await returnUrlService.saveReturnUrl(route);
        return;
      }
      if (now === 'navigate') {
        await navigateRef.current(route);
        return;
      }
      // A re-mount ends the screen's recording, as leaving it would.
      if (audioRecordingService.getRecordingState() !== 'idle') {
        await audioRecordingService.forceCancel();
        showToast.infoPersistent('Recording canceled');
      }
      const queryString = route.split('?')[1];
      const stepParam = queryString
        ? new URLSearchParams(queryString).get('step')
        : null;
      // Same request: a link without `step=` (a mention link) keeps the step
      // the user is on.
      if (stepParam) {
        setCurrentFormStep(normalizeRequestFormStepId(stepParam));
      }
      // Same request id, so `currentRequestId` stays as it is.
      (global as any).__currentRoute = route;
      setCurrentRoute(route);
      setRequestDetailMountNonce(n => n + 1);
    };

    const removeListener = deepLinkService.addListener(url => {
      const deepLinkInfo = deepLinkService.parseDeepLink(url);
      if (!deepLinkInfo) {
        // parseDeepLink has already warned why; the URL is not logged.
        return;
      }
      // The path only: the query carries request and thread ids.
      const linkPath = deepLinkInfo.route.split('?')[0];

      const action = deepLinkAction({
        route: deepLinkInfo.route,
        isAuthenticated: isAuthenticatedRef.current,
        currentRoute: currentRouteRef.current,
      });
      if (action === 'defer') {
        console.log(
          '🔒 Deep link before sign-in, saved as return URL:',
          linkPath,
        );
        // The login screen shows; the post-auth redirect lands on it.
        pendingDeepLinkRouteRef.current = deepLinkInfo.route;
        returnUrlService.saveReturnUrl(deepLinkInfo.route).catch(error => {
          console.error('Failed to save deep link return URL:', error);
        });
      } else if (action === 'reopen') {
        console.log(
          '🔗 Deep link to the request on screen, re-opening:',
          linkPath,
        );
        if (reopenInFlightRef.current) {
          return;
        }
        reopenInFlightRef.current = true;
        reopenRequestDetail(deepLinkInfo.route)
          .catch(error => {
            console.error('Failed to re-open request from deep link:', error);
          })
          .finally(() => {
            reopenInFlightRef.current = false;
          });
      } else {
        console.log('🔗 Deep link, navigating:', linkPath);
        navigateRef.current(deepLinkInfo.route).catch(error => {
          console.error('Failed to navigate from deep link:', error);
        });
      }
    });
    // Listener first, so the cold-start URL has someone to go to.
    deepLinkService.initialize();

    return removeListener;
  }, []);

  useEffect(() => {
    // Initialize offline services
    initializeOfflineServices();

    // Native: deep links are wired in their own mount-once effect above
    // (#1599 Story 3.3); this effect keeps only the offline services.
    if (Platform.OS !== 'web') {
      return () => {
        cleanupOfflineServices();
      };
    }

    // Web platform handling
    if (Platform.OS === 'web') {
      const handleRouteChange = async () => {
        const pathname = window.location.pathname;
        const search = window.location.search;
        const fullPath = pathname + search;

        if (isFeedbackCaptureOpen()) {
          if (fullPath !== currentRouteRef.current) {
            holdFeedbackNavigation(fullPath);
            window.history.pushState({}, '', currentRouteRef.current);
          }
          return;
        }

        const previousPath = currentRouteRef.current.split('?')[0];
        let nextRequestId: string | null = null;
        if (pathname === '/request-detail' && search) {
          nextRequestId = new URLSearchParams(search).get('id');
        }
        if (
          isLeavingRequestFlow(
            previousPath,
            pathname,
            currentRequestIdRef.current,
            nextRequestId,
          )
        ) {
          const guard = requestLeaveGuardRef.current;
          if (guard) {
            const mayLeave = await guard.tryLeaveRequest();
            if (!mayLeave) {
              window.history.pushState({}, '', currentRouteRef.current);
              return;
            }
          }
        }

        if (previousPath === '/preferences' && pathname !== '/preferences') {
          const mayLeave = await runPreferencesApprovalsLeaveGuard();
          if (!mayLeave) {
            window.history.pushState({}, '', currentRouteRef.current);
            return;
          }
        }

        console.log('🔗 Route change detected:', {
          pathname,
          search,
          fullPath,
          currentRequestId,
        });

        // Cancel any active recording when browser back/forward is used
        // Use infoPersistent so the toast displays AFTER navigation completes
        // Check the SERVICE state directly (not hook state) because RequestDetail has its own hook
        const serviceRecordingState = audioRecordingService.getRecordingState();
        if (serviceRecordingState !== 'idle') {
          await audioRecordingService.forceCancel();
          showToast.infoPersistent('Recording canceled');
        }

        // Check if we're navigating between different requests via browser navigation
        if (pathname === '/request-detail') {
          const urlParams = new URLSearchParams(search);
          const newRequestId = urlParams.get('id');

          // If this is a different request than current, force refresh for clean state
          if (newRequestId !== currentRequestId && currentRequestId !== null) {
            console.log(
              '🔄 Browser navigation between requests detected, forcing refresh:',
              {
                from: currentRequestId,
                to: newRequestId,
              },
            );
            window.location.reload();
            return;
          }

          setCurrentRequestId(newRequestId);
          const stepFromUrl = urlParams.get('step');
          setCurrentFormStep(
            stepFromUrl
              ? normalizeRequestFormStepId(stepFromUrl)
              : 'opportunity',
          );
          console.log(
            '🔗 Direct URL access: Setting requestId to',
            newRequestId,
            'from URL:',
            fullPath,
          );
        } else {
          setCurrentRequestId(null);
          if (pathname === '/create-request') {
            if (isFreshCreateRequestQuery(search) && user?.email) {
              await clearCreateRequestDraftSnapshot(user.email);
            }
            const urlParams = new URLSearchParams(search);
            const stepFromUrl = urlParams.get('step');
            setCurrentFormStep(
              stepFromUrl
                ? normalizeRequestFormStepId(stepFromUrl)
                : 'opportunity',
            );
          }
        }

        // Set the full path including query parameters for proper routing
        setCurrentRoute(fullPath);
      };

      handleRouteChange();

      window.addEventListener('popstate', handleRouteChange);

      return () => {
        window.removeEventListener('popstate', handleRouteChange);
        cleanupOfflineServices();
      };
    }

    return () => {
      cleanupOfflineServices();
    };
  }, [isAuthenticated, user?.email, currentRequestId]);

  const navigate = async (path: string) => {
    if (holdFeedbackNavigation(path)) {
      return;
    }

    // Parse the path to extract route and query parameters
    const [route, queryString] = path.split('?');

    const currentPath = currentRoute.split('?')[0];
    const nextPath = route;

    let nextRequestId: string | null = null;
    if (nextPath === '/request-detail' && queryString) {
      nextRequestId = new URLSearchParams(queryString).get('id');
    }
    const leavingCurrentRequest = isLeavingRequestFlow(
      currentPath,
      nextPath,
      currentRequestId,
      nextRequestId,
    );

    if (leavingCurrentRequest && requestLeaveGuard) {
      const mayLeave = await requestLeaveGuard.tryLeaveRequest();
      if (!mayLeave) {
        return;
      }
    }

    if (currentPath === '/preferences' && nextPath !== '/preferences') {
      const mayLeave = await runPreferencesApprovalsLeaveGuard();
      if (!mayLeave) {
        return;
      }
    }

    console.log('🚀 Navigation starting:', {
      path,
      route,
      queryString,
      from: currentRoute,
    });

    // Cancel any active recording before navigating
    // Use infoPersistent so the toast displays AFTER navigation completes
    // (regular toast gets lost during React re-render from setCurrentRoute)
    // Check the SERVICE state directly, not the hook state, because RequestDetail has its own
    // useAudioRecording hook and App.tsx's recordingStateRef won't track that recording
    const serviceRecordingState = audioRecordingService.getRecordingState();
    if (serviceRecordingState !== 'idle') {
      await audioRecordingService.forceCancel();
      showToast.infoPersistent('Recording canceled');
    }

    // Reset form step when navigating to create new request or request detail
    let effectivePath = path;
    if (route === '/create-request') {
      if (queryString && isFreshCreateRequestQuery(`?${queryString}`)) {
        if (user?.email) {
          await clearCreateRequestDraftSnapshot(user.email);
        }
        effectivePath = stripFreshCreateRequestParam(route, queryString);
        setCurrentFormStep('opportunity');
      } else {
        const stepParam = queryString
          ? new URLSearchParams(queryString).get('step')
          : null;
        setCurrentFormStep(
          stepParam ? normalizeRequestFormStepId(stepParam) : 'opportunity',
        );
      }
    } else if (route === '/request-detail') {
      const stepParam = queryString
        ? new URLSearchParams(queryString).get('step')
        : null;
      setCurrentFormStep(
        stepParam ? normalizeRequestFormStepId(stepParam) : 'opportunity',
      );
    }

    // Extract new request ID for comparison
    let newRequestId: string | null = null;
    if (route === '/request-detail' && queryString) {
      const urlParams = new URLSearchParams(queryString);
      newRequestId = urlParams.get('id');
    }

    // Check if we're navigating between different requests
    const isRequestNavigation =
      route === '/request-detail' &&
      newRequestId !== currentRequestId &&
      currentRequestId;
    if (isRequestNavigation && Platform.OS === 'web') {
      console.log('🔄 Request navigation detected:', {
        from: currentRequestId,
        to: newRequestId,
        clearingCaches: true,
        forceRefresh: true,
      });

      // For web platform: Force page refresh to ensure clean state
      // This is the most reliable way to ensure sidebar shows correct steps
      console.log('🔄 Forcing page refresh for clean sidebar state');
      window.location.href = path;
      return; // Exit early to prevent normal navigation
    }

    if (isRequestNavigation) {
      console.log('🔄 Request navigation detected (non-web):', {
        from: currentRequestId,
        to: newRequestId,
        clearingCaches: true,
      });

      // For non-web platforms: Clear caches and force re-render
      // Clear field permissions cache synchronously
      import('./src/services/auth/fieldPermissionService').then(
        ({ fieldPermissionService }) => {
          fieldPermissionService.clearCache();
        },
      );

      // Clear React Query cache for clean state
      queryClient.clear();
    }

    if (Platform.OS === 'web') {
      window.history.pushState({}, '', effectivePath);
      setCurrentRoute(effectivePath);

      // Extract request ID from query parameters
      if (route === '/request-detail' && queryString) {
        const urlParams = new URLSearchParams(queryString);
        const requestId = urlParams.get('id');
        console.log('🌐 Web request ID set:', requestId);
        setCurrentRequestId(requestId);
      } else {
        setCurrentRequestId(null); // Clear request ID for other routes
      }
    } else {
      // For native platforms, extract request ID from query parameters
      const fullPath = queryString ? `${route}?${queryString}` : route;
      const nativePath =
        route === '/create-request' &&
        queryString &&
        isFreshCreateRequestQuery(`?${queryString}`)
          ? stripFreshCreateRequestParam(route, queryString)
          : fullPath;
      setCurrentRoute(nativePath);
      // Store in global variable so RequestDetail can access query params
      (global as any).__currentRoute = nativePath;

      if (route === '/request-detail' && queryString) {
        const urlParams = new URLSearchParams(queryString);
        const requestId = urlParams.get('id');
        console.log('📱 Native request ID set:', requestId);
        setCurrentRequestId(requestId);
      } else {
        setCurrentRequestId(null); // Clear request ID for other routes
      }
    }
  };
  navigateRef.current = navigate;

  useEffect(() => {
    return bindFeedbackCaptureRelease(path => {
      void navigate(path);
    });
  }, [navigate]);

  useEffect(() => {
    if (Platform.OS === 'web') {
      return;
    }
    const subscription = BackHandler.addEventListener(
      'hardwareBackPress',
      () => {
        const path = currentRouteRef.current.split('?')[0];
        if (path !== '/preferences') {
          return false;
        }
        void (async () => {
          const mayLeave = await runPreferencesApprovalsLeaveGuard();
          if (mayLeave) {
            await navigate('/request');
          }
        })();
        return true;
      },
    );
    return () => subscription.remove();
  }, [runPreferencesApprovalsLeaveGuard, navigate]);

  const toggleSidebarCollapse = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  // Effect to handle post-authentication navigation and query invalidation
  useEffect(() => {
    // Detect user identity changes (impersonation start/end) and navigate immediately
    if (
      isAuthenticated &&
      user &&
      prevUserIdRef.current &&
      prevUserIdRef.current !== user.id
    ) {
      console.log(
        '🔄 User identity changed (impersonation), navigating to post-auth route',
      );
      prevUserIdRef.current = user.id;
      const postAuthRoute = roleService.getPostAuthRoute(user);
      navigate(postAuthRoute);
      return; // Skip the rest — the new identity will be handled on next render
    }
    if (user) {
      prevUserIdRef.current = user.id;
    }

    if (isAuthenticated && user && !hasInvalidatedOnAuth) {
      // Perform initial sync if needed, then invalidate queries
      const handlePostAuthSetup = async () => {
        console.log('🔄 User authenticated, performing initial sync check');

        try {
          // Import services dynamically to avoid circular dependencies
          const { syncService } = await import('./src/services/syncService');
          const { networkService } = await import('./src/services/network');

          // If online, perform initial sync to ensure offline data is synced before query reset
          if (networkService.isConnected()) {
            console.log('📡 Online at startup - performing initial sync');
            try {
              const syncResult = await syncService.fullSync();
              console.log('✅ Initial sync completed:', syncResult);

              // Invalidate sync status to clear the sync indicator
              queryClient.invalidateQueries({ queryKey: ['sync-status'] });
            } catch (error) {
              console.error('⚠️ Initial sync failed:', error);
              // Clear sync indicator even on error
              queryClient.invalidateQueries({ queryKey: ['sync-status'] });
              // Continue anyway - queries will fetch from server
            }
          } else {
            console.log('📴 Offline at startup - skipping sync');
          }
        } catch (error) {
          console.error('⚠️ Failed to import sync services:', error);
          Sentry.captureException(error);
        }

        console.log('🔄 Invalidating queries to fetch fresh data');

        // Use invalidateQueries instead of resetQueries to be less aggressive
        // This will refetch but preserve data during the fetch
        queryClient.invalidateQueries({
          queryKey: ['offline-requests'],
          exact: false,
        });

        queryClient.invalidateQueries({
          queryKey: ['requests-paginated'],
          exact: false,
        });

        setHasInvalidatedOnAuth(true);
      };

      handlePostAuthSetup();

      // Check for saved return URL (from blocked route or deep link). A deep
      // link held in memory wins: its AsyncStorage write may not have landed.
      const pendingDeepLinkRoute = pendingDeepLinkRouteRef.current;
      pendingDeepLinkRouteRef.current = null;
      (pendingDeepLinkRoute !== null
        ? Promise.resolve<string | null>(pendingDeepLinkRoute)
        : returnUrlService.getReturnUrl()
      )
        .then(savedReturnUrl => {
          if (savedReturnUrl) {
            console.log(
              '🔗 Post-auth: Found saved return URL, redirecting:',
              savedReturnUrl,
            );
            // Clear the return URL before navigating
            returnUrlService.clearReturnUrl();
            // Navigate to the saved destination
            navigate(savedReturnUrl);
            setIsInitialPageLoad(false);
            return;
          }

          // No saved return URL - check if user is already on a specific route
          const currentPathname = currentRoute.split('?')[0];
          const isOnLoginOrRoot =
            currentPathname === '/' ||
            currentPathname === '/request' ||
            currentPathname === '';
          const hasDeepLink = !isOnLoginOrRoot;

          if (isInitialPageLoad && hasDeepLink) {
            // Initial page load with deep link - preserve the URL
            console.log(
              '🔗 Initial page load with deep link, staying on current route:',
              currentRoute,
            );
            setIsInitialPageLoad(false);
          } else {
            // User logged out/in or is on root - redirect to default post-auth route
            const postAuthRoute = roleService.getPostAuthRoute(user);
            console.log(
              '🔗 Post-auth: Redirecting to default route:',
              postAuthRoute,
            );
            navigate(postAuthRoute);
            setIsInitialPageLoad(false);
          }
        })
        .catch(error => {
          console.error('Failed to retrieve return URL:', error);
          // Fallback to default behavior
          const postAuthRoute = roleService.getPostAuthRoute(user);
          navigate(postAuthRoute);
          setIsInitialPageLoad(false);
        });
    } else if (!isAuthenticated && hasInvalidatedOnAuth) {
      // Reset flag when user logs out
      setHasInvalidatedOnAuth(false);
      // Don't reset isInitialPageLoad here - a logout/login cycle should redirect
    }
  }, [isAuthenticated, user, hasInvalidatedOnAuth]);

  const renderCurrentScreen = () => {
    // Show loading screen while checking auth status
    if (authLoading) {
      return (
        <View
          style={{
            flex: 1,
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: '#f5f5f5',
          }}
        >
          <ActivityIndicator size="large" color="#FF6B35" />
        </View>
      );
    }

    // Get the pathname without query parameters for route matching
    const pathname = currentRoute.split('?')[0];

    // Public update page. No login. Do not send this path through Okta.
    if (isAppUpdateRoute(pathname)) {
      return <AppUpdate navigate={navigate} route={currentRoute} />;
    }

    // Allow success and internal user screens to be shown even when not authenticated
    if (pathname === '/access-request-success') {
      return <AccessRequestSuccess navigate={navigate} />;
    }

    if (pathname === '/internal-user-access') {
      return <InternalUserAccess navigate={navigate} />;
    }

    // OAuth callback handler for iOS simulator development mode
    if (pathname === '/auth-success') {
      // Extract sessionId from URL using regex (URLSearchParams not always available in RN)
      const sessionIdMatch = currentRoute.match(/[?&]sessionId=([^&]+)/);
      const sessionId = sessionIdMatch ? sessionIdMatch[1] : null;

      if (sessionId) {
        console.log('🔐 OAuth callback received with sessionId:', sessionId);
        // The AuthContext will handle session validation on mount
        // Redirect to home after a brief moment
        setTimeout(() => navigate('/'), 100);
      }

      return (
        <View
          style={{
            flex: 1,
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: '#f5f5f5',
          }}
        >
          <ActivityIndicator size="large" color="#CC0000" />
          <Text style={{ marginTop: 16, fontSize: 16, color: '#333' }}>
            Completing authentication...
          </Text>
        </View>
      );
    }

    // Authenticated but no ITS role — same login shell with access message (ITS_TC_101)
    if (isAuthenticated && user && !roleService.isAuthorizedUser(user)) {
      return <Login accessNotAuthorized />;
    }

    if (!isAuthenticated) {
      // If user is trying to access a protected route (not login/root), save it for post-auth redirect
      if (pathname !== '/' && pathname !== '/request' && pathname !== '') {
        console.log(
          '🔒 User not authenticated, saving return URL:',
          currentRoute,
        );

        // Save the intended destination for post-auth redirect
        returnUrlService.saveReturnUrl(currentRoute).catch(error => {
          console.error('Failed to save return URL:', error);
        });

        // Redirect to root/login
        if (Platform.OS === 'web') {
          // Use replaceState to change URL without adding to history
          window.history.replaceState({}, '', '/');
        }
      }
      return <Login />;
    }

    // Authenticated screens
    switch (pathname) {
      case '/request':
      case '/':
        return (
          <ViewRequests
            key={user?.email || 'default'}
            navigate={navigate}
            currentPath={currentRoute}
          />
        );
      case '/users':
      case '/role-management':
        return (
          <RoleManagement navigate={navigate} currentPath={currentRoute} />
        );
      case '/prompt-management':
        if (!featureFlags.promptManagement) {
          return <UnauthorizedAccess />;
        }
        return (
          <PromptManagement navigate={navigate} currentPath={currentRoute} />
        );
      case '/feedback-view':
        return <FeedbackView navigate={navigate} currentPath={currentRoute} />;
      case '/request-versions':
        return (
          <VersionHistory navigate={navigate} currentPath={currentRoute} />
        );
      case '/profile':
        return <UserProfile navigate={navigate} currentPath={currentRoute} />;
      case '/preferences':
        return <Preferences navigate={navigate} currentPath={currentRoute} />;
      case '/terms-conditions':
        return (
          <TermsConditionsView navigate={navigate} currentPath={currentRoute} />
        );
      case '/request-detail':
        return (
          <RequestDetail
            key={`request-detail-${requestDetailMountNonce}`}
            navigate={navigate}
            sidebarCollapsed={sidebarCollapsed}
            requestId={currentRequestId}
            currentStep={currentFormStep}
            onStepChange={setCurrentFormStepWithPersistence}
            onStageChange={handleStageChange}
            onEffectiveStageChange={handleEffectiveStageChange}
          />
        );
      case '/create-request':
        return (
          <CreateRequestWithNavScope
            navigate={navigate}
            sidebarCollapsed={sidebarCollapsed}
            currentStep={currentFormStep}
            onStepChange={setCurrentFormStepWithPersistence}
            recordingState={recordingState}
            elapsedTime={elapsedTime}
            onStartRecording={handleStartRecording}
            onPauseRecording={handlePauseRecording}
            onResumeRecording={handleResumeRecording}
            onAcceptRecording={handleAcceptRecording}
            onDiscardRecording={handleDiscardRecording}
            recordedAudioFile={recordedAudioFile}
          />
        );
      case '/unauthorized':
        return <UnauthorizedAccess />;
      default:
        return (
          <ViewRequests
            key={user?.email || 'default'}
            navigate={navigate}
            currentPath={currentRoute}
          />
        );
    }
  };

  // Wrapper to conditionally apply Suspense only on web
  const renderScreenWithSuspense = () => {
    if (Platform.OS === 'web') {
      return (
        <Suspense fallback={<LoadingFallback />}>
          {renderCurrentScreen()}
        </Suspense>
      );
    }
    // Native doesn't need Suspense since we're using regular imports
    return renderCurrentScreen();
  };

  const pathname = currentRoute.split('?')[0];
  const shouldShowSidebar =
    isAuthenticated &&
    (pathname === '/request-detail' || pathname === '/create-request');
  const isITAdministrator = roleService.isITAdministrator(user);

  // Check if user needs to accept Terms and Conditions
  // Native: Full-screen blocking view
  // Web: Overlay modal on top of main content (request list visible behind)
  // Skip during impersonation — admins should not accept T&C on behalf of another user
  const showTCModal =
    isAuthenticated &&
    roleService.isAuthorizedUser(user) &&
    !authLoading &&
    tcRequiresAcceptance &&
    !tcLoading &&
    !isImpersonating;

  if (showTCModal && Platform.OS !== 'web') {
    // Native: Render T&C as full-screen replacement
    return (
      <SafeAreaProvider style={{ flex: 1 }}>
        <StatusBar barStyle={isDarkMode ? 'light-content' : 'dark-content'} />
        <TermsConditionsModal />
      </SafeAreaProvider>
    );
  }

  // Web: Render main content with optional T&C overlay
  return (
    <>
      <AppContentWithQuery
        {...{
          isDarkMode,
          currentRoute,
          sidebarCollapsed,
          leftSidebarVisible,
          screenWidth,
          isAuthenticated,
          user,
          authLoading,
          currentRequestId,
          currentFormStep,
          currentRequestStage,
          effectiveStageForSidebar,
          handleStageChange,
          handleEffectiveStageChange,
          isOnRequestDetail,
          navigate,
          toggleSidebarCollapse,
          renderCurrentScreen: renderScreenWithSuspense,
          shouldShowSidebar,
          isITAdministrator,
          isMobileLayout,
          setLeftSidebarVisible,
          setCurrentFormStep: setCurrentFormStepWithPersistence,
          recordingState,
          elapsedTime,
          handleStartRecording,
          handlePauseRecording,
          handleResumeRecording,
          handleAcceptRecording,
          handleDiscardRecording,
        }}
      />
      {/* Web: T&C modal renders as overlay on top of content */}
      {showTCModal && Platform.OS === 'web' && <TermsConditionsModal />}
    </>
  );
}

function AppContentWithQuery(props: any) {
  const {
    isDarkMode,
    currentRoute,
    sidebarCollapsed,
    leftSidebarVisible,
    screenWidth,
    // Authentication props (not directly used in this component, but passed to renderCurrentScreen)
    isAuthenticated: _isAuthenticated,
    user: _user,
    authLoading: _authLoading,
    currentRequestId,
    currentFormStep,
    currentRequestStage,
    effectiveStageForSidebar,
    // Stage change handlers (not directly used, but passed to renderCurrentScreen)
    handleStageChange: _handleStageChange,
    handleEffectiveStageChange: _handleEffectiveStageChange,
    isOnRequestDetail,
    navigate,
    toggleSidebarCollapse,
    renderCurrentScreen,
    shouldShowSidebar,
    isITAdministrator,
    isMobileLayout,
    setLeftSidebarVisible,
    setCurrentFormStep: setCurrentFormStepWithPersistence,
    // Recording state and handlers
    recordingState,
    elapsedTime,
    handleStartRecording,
    handlePauseRecording,
    handleResumeRecording,
    handleAcceptRecording,
    handleDiscardRecording,
  } = props;
  // Get request stage immediately for sidebar permission management
  const { stage: detectedStage, isLoading: isStageLoading } = useRequestStage(
    currentRequestId,
    isOnRequestDetail,
  );

  // Use detected stage when available, otherwise use manually set stage
  // For request detail pages: wait for stage detection to complete to avoid showing wrong permissions
  // If effectiveStageForSidebar is set (from basic/advanced view toggle), use that for sidebar filtering
  const baseEffectiveStage = isOnRequestDetail
    ? isStageLoading
      ? undefined
      : detectedStage // Use detected stage when ready, undefined while loading
    : currentRequestStage; // Use current stage for non-detail pages (like create-request)

  const effectiveRequestStage =
    effectiveStageForSidebar !== undefined
      ? effectiveStageForSidebar // Use effective stage from RequestDetail (for basic/advanced view)
      : baseEffectiveStage; // Otherwise use base effective stage

  return (
    <SafeAreaProvider style={{ flex: 1 }}>
      <StatusBar barStyle={isDarkMode ? 'light-content' : 'dark-content'} />
      <View ref={feedbackPageRef} collapsable={false} style={{ flex: 1 }}>
        {shouldShowSidebar ? (
          <RequestLayoutShell
            isMobileLayout={isMobileLayout}
            renderCurrentScreen={renderCurrentScreen}
            navigate={navigate}
            currentRoute={currentRoute}
            recordingState={recordingState}
            currentRequestId={currentRequestId}
            currentFormStep={currentFormStep}
            setCurrentFormStep={setCurrentFormStepWithPersistence}
            effectiveRequestStage={effectiveRequestStage}
            isITAdministrator={isITAdministrator}
            sidebarCollapsed={sidebarCollapsed}
            toggleSidebarCollapse={toggleSidebarCollapse}
            leftSidebarVisible={leftSidebarVisible}
            setLeftSidebarVisible={setLeftSidebarVisible}
          />
        ) : (
          renderCurrentScreen()
        )}
      </View>
      <FeedbackCaptureHost />
      {/* Web toast component */}
      {/* Above RN Modal overlays (dialogs use ~1000); keep below extreme portal locks */}
      {Platform.OS === 'web' && Toaster && (
        <Toaster
          position="top-center"
          containerStyle={{
            top: 80,
            zIndex: 10000,
          }}
          toastOptions={{
            duration: 2500,
            style: {
              background: '#333',
              color: '#fff',
              cursor: 'pointer',
            },
            success: {
              style: {
                background: '#4ade80',
                cursor: 'pointer',
              },
            },
            error: {
              style: {
                background: '#ef4444',
                cursor: 'pointer',
              },
            },
            loading: {
              style: {
                background: '#3b82f6',
                cursor: 'pointer',
              },
            },
          }}
        />
      )}
      {/* Native toast component - only render on native platforms */}
      {/* Wrapped in a View with pointerEvents="box-none" to prevent blocking interactions */}
      {Platform.OS !== 'web' && Toast && (
        <View
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 9999,
            elevation: 9999,
          }}
          pointerEvents="box-none"
        >
          <Toast
            config={{
              error: (props: any) => {
                const { text1, text2 } = props;
                return (
                  <View
                    style={{
                      backgroundColor: '#fee',
                      borderLeftWidth: 4,
                      borderLeftColor: '#c00',
                      paddingHorizontal: 16,
                      paddingVertical: 12,
                      marginHorizontal: 16,
                      marginTop: 8,
                      borderRadius: 8,
                      maxWidth: screenWidth - 32,
                      minWidth: Math.min(320, screenWidth - 32),
                      shadowColor: '#000',
                      shadowOffset: { width: 0, height: 2 },
                      shadowOpacity: 0.25,
                      shadowRadius: 3.84,
                      elevation: 5,
                    }}
                    pointerEvents="auto"
                  >
                    <Text
                      style={{
                        fontSize: 14,
                        fontWeight: '600',
                        color: '#c00',
                        flexWrap: 'wrap',
                        flexShrink: 1,
                      }}
                      numberOfLines={0}
                    >
                      {text1}
                    </Text>
                    {text2 && (
                      <Text
                        style={{
                          fontSize: 13,
                          fontWeight: '400',
                          color: '#900',
                          marginTop: 4,
                          flexWrap: 'wrap',
                          flexShrink: 1,
                        }}
                        numberOfLines={0}
                      >
                        {text2}
                      </Text>
                    )}
                  </View>
                );
              },
              success: (props: any) => {
                const { text1 } = props;
                return (
                  <View
                    style={{
                      backgroundColor: '#efe',
                      borderLeftWidth: 4,
                      borderLeftColor: '#0a0',
                      paddingHorizontal: 16,
                      paddingVertical: 12,
                      marginHorizontal: 16,
                      marginTop: 8,
                      borderRadius: 8,
                      maxWidth: screenWidth - 32,
                      minWidth: Math.min(320, screenWidth - 32),
                      shadowColor: '#000',
                      shadowOffset: { width: 0, height: 2 },
                      shadowOpacity: 0.25,
                      shadowRadius: 3.84,
                      elevation: 5,
                    }}
                    pointerEvents="auto"
                  >
                    <Text
                      style={{
                        fontSize: 14,
                        fontWeight: '600',
                        color: '#0a0',
                        flexWrap: 'wrap',
                        flexShrink: 1,
                      }}
                      numberOfLines={0}
                    >
                      {text1}
                    </Text>
                  </View>
                );
              },
              info: (props: any) => {
                const { text1 } = props;
                return (
                  <View
                    style={{
                      backgroundColor: '#fff3cd',
                      borderLeftWidth: 4,
                      borderLeftColor: '#ffc107',
                      paddingHorizontal: 16,
                      paddingVertical: 12,
                      marginHorizontal: 16,
                      marginTop: 8,
                      borderRadius: 8,
                      maxWidth: screenWidth - 32,
                      minWidth: Math.min(320, screenWidth - 32),
                      shadowColor: '#000',
                      shadowOffset: { width: 0, height: 2 },
                      shadowOpacity: 0.25,
                      shadowRadius: 3.84,
                      elevation: 5,
                    }}
                    pointerEvents="auto"
                  >
                    <Text
                      style={{
                        fontSize: 14,
                        fontWeight: '500',
                        color: '#856404',
                        textAlign: 'center',
                        flexWrap: 'wrap',
                        flexShrink: 1,
                      }}
                      numberOfLines={0}
                    >
                      {text1}
                    </Text>
                  </View>
                );
              },
            }}
          />
        </View>
      )}
    </SafeAreaProvider>
  );
}

function App() {
  const content = (
    <SafeAreaProvider>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <ImpersonationProvider>
            <TermsConditionsProvider>
              <FormEditingProvider>
                <SidebarStepMetricsProvider>
                  <LocationTagsDraftProvider>
                    <RequestLeaveGuardProvider>
                      <PreferencesApprovalsLeaveGuardProvider>
                        <UOMProvider>
                          <AppContent />
                          <AppUpdateScreen />
                          {/* FlavorBanner renders LAST so its modal is on top of everything */}
                          <FlavorBanner />
                        </UOMProvider>
                      </PreferencesApprovalsLeaveGuardProvider>
                    </RequestLeaveGuardProvider>
                  </LocationTagsDraftProvider>
                </SidebarStepMetricsProvider>
              </FormEditingProvider>
            </TermsConditionsProvider>
          </ImpersonationProvider>
        </AuthProvider>
      </QueryClientProvider>
    </SafeAreaProvider>
  );

  // Wrap with KeyboardProvider on native platforms
  if (KeyboardProvider) {
    return <KeyboardProvider>{content}</KeyboardProvider>;
  }
  return content;
}

export default Sentry.wrap(App);
